/**
 * Performance monitoring utilities
 */

// Web Vitals tracking
export const trackWebVitals = () => {
  // Track Core Web Vitals if supported
  if ('PerformanceObserver' in window) {
    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          event_category: 'performance',
          event_label: 'LCP',
          value: Math.round(lastEntry.startTime)
        });
      }
      
      console.log('📊 LCP:', Math.round(lastEntry.startTime), 'ms');
    });
    
    try {
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP observer not supported');
    }

    // First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const fid = entry.processingStart - entry.startTime;
        
        if (window.gtag) {
          window.gtag('event', 'web_vitals', {
            event_category: 'performance',
            event_label: 'FID',
            value: Math.round(fid)
          });
        }
        
        console.log('📊 FID:', Math.round(fid), 'ms');
      });
    });
    
    try {
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      console.warn('FID observer not supported');
    }

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      
      if (window.gtag) {
        window.gtag('event', 'web_vitals', {
          event_category: 'performance',
          event_label: 'CLS',
          value: Math.round(clsValue * 1000)
        });
      }
      
      console.log('📊 CLS:', clsValue.toFixed(4));
    });
    
    try {
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      console.warn('CLS observer not supported');
    }
  }
};

// Bundle size tracking
export const trackBundleSize = () => {
  if ('performance' in window && 'getEntriesByType' in performance) {
    const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    
    let totalJSSize = 0;
    let totalCSSSize = 0;
    
    resources.forEach((resource) => {
      if (resource.name.includes('.js')) {
        totalJSSize += resource.transferSize || 0;
      } else if (resource.name.includes('.css')) {
        totalCSSSize += resource.transferSize || 0;
      }
    });
    
    console.log('📦 Bundle sizes:', {
      js: `${(totalJSSize / 1024).toFixed(1)} KB`,
      css: `${(totalCSSSize / 1024).toFixed(1)} KB`,
      total: `${((totalJSSize + totalCSSSize) / 1024).toFixed(1)} KB`
    });
    
    if (window.gtag) {
      window.gtag('event', 'bundle_size', {
        event_category: 'performance',
        event_label: 'total_kb',
        value: Math.round((totalJSSize + totalCSSSize) / 1024)
      });
    }
  }
};

// Memory usage tracking
export const trackMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
    
    console.log('💾 Memory usage:', {
      used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(1)} MB`,
      total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(1)} MB`,
      limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(1)} MB`
    });
    
    if (window.gtag) {
      window.gtag('event', 'memory_usage', {
        event_category: 'performance',
        event_label: 'used_mb',
        value: Math.round(memory.usedJSHeapSize / 1024 / 1024)
      });
    }
  }
};

// Initialize performance monitoring
export const initPerformanceMonitoring = () => {
  // Track initial page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      trackWebVitals();
      trackBundleSize();
      trackMemoryUsage();
    }, 1000);
  });
  
  // Track memory usage periodically
  setInterval(trackMemoryUsage, 30000); // Every 30 seconds
};

// Performance debugging helpers
export const logRenderTime = (componentName: string, startTime: number) => {
  const renderTime = performance.now() - startTime;
  
  if (renderTime > 16) { // Longer than one frame (60fps)
    console.warn(`⚠️ Slow render: ${componentName} took ${renderTime.toFixed(2)}ms`);
  } else {
    console.log(`✅ Fast render: ${componentName} took ${renderTime.toFixed(2)}ms`);
  }
  
  return renderTime;
};